import { SlideUpdate } from 'api/slides';
import { CaseUpdate } from 'api/study';
import {
  concat,
  filter,
  find,
  flatMap,
  forEach,
  includes,
  isEmpty,
  map,
  omit,
  partition,
  size,
  some,
} from 'es-toolkit/compat';
import { Procedure } from 'interfaces/procedure';
import { Slide } from 'interfaces/slide';

import {
  assignManifestDraftCaseIds,
  buildOwnCaseManifestDraft,
  buildSingleCaseManifestDraft,
  buildUnassignedCaseManifestDraft,
  ManifestCaseTarget,
  ManifestDialogUrlState,
  ManifestDraftCase,
} from './dialogState';
import {
  applyManifestDraftPatches,
  collectCaseSharedMetadataErrors,
  collectDuplicateSlideRowErrors,
  collectManifestCaseLabelErrors,
  combineManifestCasesWithExistingCases,
  mergeManifestCases,
  removeSlidesFromManifestCases,
  resolveManifestExistingCaseAssignments,
} from './manifestResolution';

export const applyManifestSlideEdits = (
  previousState: ManifestDialogUrlState,
  changes: Partial<SlideUpdate>,
  slideIds: string[]
): ManifestDialogUrlState => {
  const nextSlidePatches = { ...(previousState.slidePatchesById || {}) };
  forEach(slideIds, (slideId) => {
    nextSlidePatches[slideId] = { ...(nextSlidePatches[slideId] || {}), ...changes };
  });
  return { ...previousState, slidePatchesById: nextSlidePatches };
};

// Draft cases that share a label are displayed as one case, so a label edit must follow the edited slide, not its displayed case.
const applyManifestSlideLabelEdit = ({
  previousState,
  label,
  slideId,
  caseId,
  currentDraftCases,
  baseManifestCases,
}: {
  previousState: ManifestDialogUrlState;
  label: string;
  slideId: string;
  caseId: number;
  currentDraftCases: Procedure[];
  baseManifestCases: Procedure[];
}): ManifestDialogUrlState => {
  const rawCase =
    find(baseManifestCases, (candidate) => candidate.id === caseId && some(candidate.slides, { id: slideId })) ??
    find(baseManifestCases, (candidate) => some(candidate.slides, { id: slideId }));
  if (!rawCase) return previousState;
  const casePatchesById = { ...(previousState.casePatchesById || {}) };
  if (size(rawCase.slides) === 1) {
    casePatchesById[rawCase.id] = { ...casePatchesById[rawCase.id], label };
    return { ...previousState, casePatchesById };
  }
  const [detachedCase] = assignManifestDraftCaseIds({
    cases: [{ ...rawCase, slides: filter(rawCase.slides, { id: slideId }) }],
    retainedCases: concat(previousState.draftCases || [], currentDraftCases),
    casePatchesById,
  });
  casePatchesById[detachedCase.id] = { ...casePatchesById[rawCase.id], label };
  return {
    ...previousState,
    casePatchesById,
    draftCases: concat(
      (previousState.draftCases || []).flatMap((procedure) =>
        procedure.id === rawCase.id ? removeSlidesFromManifestCases([procedure], [slideId]) : [procedure]
      ),
      detachedCase
    ),
  };
};

export const applyManifestCaseEdits = ({
  previousState,
  changes,
  caseIds,
  slideId,
  currentDraftCases,
  baseManifestCases,
  isCaseLabelEditable,
}: {
  previousState: ManifestDialogUrlState;
  changes: Partial<CaseUpdate>;
  caseIds: number[];
  slideId?: string;
  currentDraftCases: Procedure[];
  baseManifestCases: Procedure[];
  isCaseLabelEditable: (slideId: string, caseId?: number) => boolean;
}): ManifestDialogUrlState => {
  const nextCasePatches = { ...(previousState.casePatchesById || {}) };
  const { label, ...metadataChanges } = changes;
  const isSlideScopedLabelEdit = label !== undefined && slideId !== undefined;
  const hasCaseLevelChanges = !isEmpty(metadataChanges) || (label !== undefined && !isSlideScopedLabelEdit);
  forEach(hasCaseLevelChanges ? caseIds : [], (caseId) => {
    if (!isEmpty(metadataChanges)) {
      nextCasePatches[caseId] = { ...(nextCasePatches[caseId] || {}), ...metadataChanges };
    }
    const displayedCase = find(currentDraftCases, { id: caseId });
    forEach(baseManifestCases, (rawCase) => {
      // A raw case shown as its own row is not a constituent of this one, even when they share a slide.
      const isSeparateDisplayedCase = rawCase.id !== caseId && some(currentDraftCases, { id: rawCase.id });
      if (
        !isSeparateDisplayedCase &&
        some(
          rawCase.slides,
          (slide) => isCaseLabelEditable(slide.id, rawCase.id) && some(displayedCase?.slides, { id: slide.id })
        )
      ) {
        nextCasePatches[rawCase.id] = {
          ...nextCasePatches[rawCase.id],
          ...metadataChanges,
          ...(label === undefined || isSlideScopedLabelEdit ? {} : { label }),
        };
      }
    });
  });
  const nextState = { ...previousState, casePatchesById: nextCasePatches };
  if (!isSlideScopedLabelEdit || !isCaseLabelEditable(slideId, caseIds[0])) return nextState;
  return applyManifestSlideLabelEdit({
    previousState: nextState,
    label,
    slideId,
    caseId: caseIds[0],
    currentDraftCases,
    baseManifestCases,
  });
};

export const addManifestDraftCases = (
  previousState: ManifestDialogUrlState,
  casesToAdd: Procedure[],
  targetCase: ManifestCaseTarget = 'new'
): ManifestDialogUrlState => {
  const addedSlideIds = map(flatMap(casesToAdd, 'slides'), 'id');
  const newDraftCases = assignManifestDraftCaseIds({
    cases: casesToAdd,
    retainedCases: previousState.draftCases || [],
    casePatchesById: previousState.casePatchesById,
  });
  const nextExistingCaseAssignments = { ...(previousState.existingCaseAssignmentsBySlideId || {}) };
  forEach(addedSlideIds, (slideId) => {
    nextExistingCaseAssignments[slideId] = targetCase;
  });
  return {
    ...previousState,
    existingCaseAssignmentsBySlideId: nextExistingCaseAssignments,
    pendingSlidesSelectionAction: undefined,
    removedSlideIds: filter(previousState.removedSlideIds || [], (slideId) => !includes(addedSlideIds, slideId)),
    // Removal only masks stored rows, so re-added slides must leave their original rows before the mask is cleared.
    draftCases: concat(removeSlidesFromManifestCases(previousState.draftCases || [], addedSlideIds), newDraftCases),
  };
};

export const addHydratedManifestSlides = ({
  previousState,
  slides,
  labId,
  groupIntoSingleCase,
}: {
  previousState: ManifestDialogUrlState;
  slides: Slide[];
  labId: string;
  groupIntoSingleCase: boolean;
}): ManifestDialogUrlState => {
  const slideIds = map(slides, 'id');
  // A slide added again replaces its stored row, so it never appears twice in the plan.
  const retainedCases = removeSlidesFromManifestCases(previousState.draftCases || [], slideIds);
  // A null target means the user chose to assign this slide themselves, so it keeps an empty label.
  const [unassignedSlides, assignedSlides] = partition(
    slides,
    (slide) => previousState.existingCaseAssignmentsBySlideId?.[slide.id] === null
  );
  // Each explicit group is its own draft case, so it never merges with another group by label.
  const manualCases = groupIntoSingleCase
    ? concat(
        retainedCases,
        assignManifestDraftCaseIds({
          cases: buildSingleCaseManifestDraft({ slides: assignedSlides, labId }),
          retainedCases,
          casePatchesById: previousState.casePatchesById,
        })
      )
    : mergeManifestCases(retainedCases, buildOwnCaseManifestDraft({ slides: assignedSlides, labId }));
  const unassignedCases = assignManifestDraftCaseIds({
    cases: buildUnassignedCaseManifestDraft({ slides: unassignedSlides, labId }),
    retainedCases: manualCases,
    casePatchesById: previousState.casePatchesById,
  });
  return {
    ...previousState,
    draftCases: concat(manualCases, unassignedCases),
    removedSlideIds: filter(previousState.removedSlideIds || [], (slideId) => !includes(slideIds, slideId)),
  };
};

export const resolveManifestDraftCases = ({
  manifestCases,
  existingProcedures,
  overrideCases,
  targetsBySlideId,
}: {
  manifestCases: ManifestDraftCase[];
  existingProcedures: Procedure[];
  overrideCases: boolean;
  targetsBySlideId?: Readonly<Record<string, ManifestCaseTarget | undefined>>;
}) => {
  if (
    !isEmpty(collectManifestCaseLabelErrors(manifestCases, existingProcedures)) ||
    !isEmpty(collectDuplicateSlideRowErrors(manifestCases)) ||
    !isEmpty(collectCaseSharedMetadataErrors(manifestCases)) ||
    manifestCases.some((procedure) => procedure.draftIssues?.length)
  )
    return manifestCases;
  const mergedManifestCases = mergeManifestCases([], manifestCases);

  // A draft case the user explicitly targeted at "New case" must not absorb the slides of a saved case that shares its label.
  const isExplicitNewDraftCase = (manifestCase: Procedure) =>
    !some(existingProcedures, { id: manifestCase.id }) &&
    some(manifestCase.slides, (slide) => targetsBySlideId?.[slide.id] === 'new');

  return overrideCases
    ? mergedManifestCases
    : combineManifestCasesWithExistingCases(mergedManifestCases, existingProcedures, isExplicitNewDraftCase).cases;
};

export const resolveManifestDraftAssignments = ({
  cases,
  existingProcedures,
  assignmentsBySlideId,
  casePatchesById,
  slidePatchesById,
  labId,
  studyId,
}: Parameters<typeof resolveManifestExistingCaseAssignments>[0] & {
  slidePatchesById: Record<string, Partial<SlideUpdate>>;
}) => {
  const patchedManifestCases = applyManifestDraftPatches(cases, casePatchesById, slidePatchesById);
  if (
    !isEmpty(collectDuplicateSlideRowErrors(patchedManifestCases)) ||
    !isEmpty(collectCaseSharedMetadataErrors(patchedManifestCases)) ||
    patchedManifestCases.some((procedure) => procedure.draftIssues?.length)
  ) {
    return { cases: patchedManifestCases, missingTargetCount: 0 };
  }
  const targetCasePatches = { ...casePatchesById };
  const importedTargets = new Set<number>();
  forEach(patchedManifestCases, (procedure) => {
    const target = find(existingProcedures, { label: procedure.label });
    if (
      !target ||
      importedTargets.has(target.id) ||
      !some(procedure.slides, (slide) => assignmentsBySlideId[slide.id] === target.id)
    )
      return;
    importedTargets.add(target.id);
    targetCasePatches[target.id] = {
      ...omit(casePatchesById[procedure.id] || {}, ['label']),
      ...casePatchesById[target.id],
    };
  });
  return resolveManifestExistingCaseAssignments({
    cases: patchedManifestCases,
    existingProcedures,
    assignmentsBySlideId,
    casePatchesById: targetCasePatches,
    labId,
    studyId,
  });
};
